import React, { useState } from 'react';
import { IconUploadOutline } from '@saucelabs/ds-icons';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import accepts from 'attr-accept';
import { Loader } from '@saucelabs/sl-component-loader';
import { constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';
import { Typography } from '@saucelabs/ds-typography';

import UploadFileButton from './UploadFileButton';

const ALLOWED_FILE_TYPES = '.apk,.ipa,.zip,.aab';

const AppUploadBox = ({
  onSelectFile,
  onUploadClick,
  uploadingFiles,
}) => {
  const [isDragActive, setDragActive] = useState(false);

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.target === event.currentTarget) {
      setDragActive(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault(); // This is required to make "drop" events fire
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files && event.dataTransfer.files.length) {
      const file = event.dataTransfer.files[0];

      if (accepts(file, ALLOWED_FILE_TYPES)) {
        onSelectFile(file);
        onUploadClick(true);
      }
      setDragActive(false);
    }
  };

  return (
    <div className="m-0 border border-dashed border-blue-700 text-gray-600">
      <div
        className="relative"
        data-qa="manual-app-upload-box-overlay-wrapper"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragActive && (
          <div className="absolute inset-0 flex justify-center items-center pointer-events-none bg-gray-50">
            <em>Drop here</em>
          </div>
        )}
        <div className={classnames('p-3.5', {
          'pb-2.5': !!uploadingFiles.length,
          'pointer-events-none': isDragActive,
        })}
        >
          <IconUploadOutline className="w-4 h-4 mb-0.5 text-blue-400 fill-current" />
          <p>
            <span>Drag & drop or</span>
          </p>
          <UploadFileButton
            acceptTypes={ALLOWED_FILE_TYPES.split(',')}
            isDropzoneOpen
            onSelectFile={onSelectFile}
            onUploadClick={onUploadClick}
          />
          <div className="px-2.5 pt-2.5">
            <Typography className="mb-0.75">
              {'Supported files are .apk, .aab, .ipa, or .zip with a valid '}
              <a
                href="https://docs.saucelabs.com/mobile-apps/app-storage/#accepted-file-types"
                rel="noreferrer"
                target="_blank"
              >
                app&nbsp;bundle
              </a>
              {'.'}
            </Typography>
            <Typography>Apps will be stored for 60 days and shared with your team.</Typography>
          </div>
          {!!uploadingFiles.length && (
            <>
              <hr className="border-solid border-b-0 border border-gray-200 mt-3 mb-3" />
              <div className="flex flex-col items-start text-high-emphasis">
                <strong className="mb-1">Upload in progress</strong>
                {uploadingFiles.map(({ displayName, progress, fileSize, status }) => (
                  <div
                    key={displayName}
                    className="flex items-center mb-1 font-normal break-words"
                  >
                    <Loader
                      className="mr-1"
                      fontSize={1}
                      show
                    />
                    <p className="m-0 text-left">
                      <span>{`${displayName} (${fileSize}) • `}</span>
                      <span className="text-green-400">
                        {(status === appStorageConstants.APP_UPLOAD_STATUS.PROCESSING) ?
                          'Processing' :
                          `${progress}%`}
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

AppUploadBox.propTypes = {
  onSelectFile: PropTypes.func.isRequired,
  onUploadClick: PropTypes.func.isRequired,
  uploadingFiles: PropTypes.arrayOf(PropTypes.object),
};

AppUploadBox.defaultProps = {
  uploadingFiles: [],
};

export default AppUploadBox;
